/**
 * Cliente de los 8 endpoints de `/admin/llm-models` y `/admin/llm-assignments`.
 *
 * Fichero propio (y no mas metodos en `chatApi.ts`) porque son 8 endpoints con
 * su propio vocabulario, pero **no monta otro cliente HTTP**: recibe el
 * `fetchWithAuth` de `chatApi.ts` por parametro. Duplicar el retry del 401 y el
 * `onAuthFailure` daria dos comportamientos de autenticacion que divergen en la
 * primera caducidad de token.
 */
import { ApiError } from "./chatApi";

/** `fetchWithAuth` de `chatApi.ts`. Se inyecta para no duplicar auth ni retry. */
export type FetchAuth = (url: string, init?: RequestInit) => Promise<Response>;

/** Precios en USD por MILLON de tokens. `null` = OpenRouter no lo declara. */
export interface PreciosModelo {
  input: number | null;
  output: number | null;
  cached_input: number | null;
}

/**
 * Entrada del buscador del catalogo de OpenRouter (`GET /llm-models/catalog`).
 *
 * Deliberadamente distinta de `ModeloCatalogo` (`chatApi.ts`), que es la vista
 * MINIMA del selector del chat (`model_id` + `label`) y la sirve `/assistants`.
 * No es una duplicacion: aqui hay precios y capacidades que aquella no tiene y
 * no debe tener. Lo que NO se hace es redeclarar `ModeloCatalogo`.
 */
export interface ModeloDelCatalogo {
  model_id: string;
  name: string | null;
  context_length: number | null;
  pricing: PreciosModelo;
  supports_tools: boolean;
  supports_json: boolean;
}

/**
 * Respuesta del buscador del catalogo.
 *
 * `modoDirecto` viaja SIEMPRE, tambien con resultados: derivarlo de "la lista
 * vino vacia" confundiria "no hay modelos de ese proveedor en este modo" con
 * "no existe ninguno que se llame asi", que son cosas distintas y llevan a
 * acciones distintas (configurar una clave, o escribir otra cosa).
 */
export interface ResultadoCatalogo {
  modelos: ModeloDelCatalogo[];
  modoDirecto: boolean;
}

/** Fila del catalogo curado (`GET /llm-models`). */
export interface ModeloCurado extends ModeloDelCatalogo {
  label: string | null;
  enabled: boolean;
  /**
   * Cuando se refresco el snapshot contra OpenRouter. **NULLABLE**: `null` es
   * "nunca sincronizado", que es un estado legitimo y DISTINTO de "viejo" —
   * ver `estadoSincronizacion`.
   */
  synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/**
 * Tipo de target de una asignacion, en UN SOLO sitio.
 *
 * Estaba escrito a mano en tres puntos del fichero, y al aparecer `subagent`
 * eso habrian sido tres sitios donde olvidar el valor nuevo: el compilador
 * dejaria pasar una llamada con el tipo viejo mientras la lista SI lo trae.
 */
export type TargetType = "agent" | "operation" | "subagent";

/**
 * Origen del modelo efectivo de un target.
 *
 * Son TRES y no dos desde que hay subagentes: `heredado` (el subagente no tiene
 * fila propia pero su agente padre SI tiene asignacion) no es lo mismo que
 * `default` (ni el uno ni el otro), y el panel tiene que poder decirlo. Ver
 * `origenConocido` para el trato de un valor que este front no conozca.
 */
export type OrigenAsignacion = "default" | "asignado" | "heredado";

/**
 * Como esta CONSTRUIDO un agente. Lo declara `register_graph(tipo=...)`.
 *
 * `react` = agente ReAct de langchain; `deep` = deep agent que ademas orquesta
 * subagentes; `grafo` = un `StateGraph` con nodos propios, donde el LLM razona
 * dentro de cada nodo y no hay bucle ReAct.
 */
export type TipoAgente = "react" | "deep" | "grafo";

/**
 * Lo que el panel pinta como insignias en la caja de un agente.
 *
 * Describe al AGENTE, no a su asignacion: no cambia cuando un admin toca un
 * modelo. Por eso viaja agrupado y no suelto entre `model_id` y `origen`.
 *
 * Las **operaciones auxiliares la traen a `null`**, y ese `null` es informacion:
 * una operacion no es un agente, asi que no es "no conversacional" ni "sin
 * HITL" — sencillamente no aplica, y pintarle esas insignias en `false` diria
 * algo falso sobre ella.
 */
export interface CaracteristicasAgente {
  tipo: TipoAgente;
  /** Esta en el selector del chat. `false` = solo se dispara por API o batch. */
  conversacional: boolean;
  /** Puede generar aprobaciones (interrupt HITL). */
  hitl: boolean;
  /** Su input EXIGE un documento adjunto. */
  requires_document: boolean;
  num_subagentes: number;
}

/**
 * Normaliza el `tipo` que llega del backend.
 *
 * Mismo criterio que `origenConocido`: un valor que este front no conozca no
 * puede dejar la caja a medias. Se devuelve `null` y **no se pinta insignia de
 * tipo**, en vez de caer a `react` — inventarse el tipo mas comun afirmaria
 * como ReAct un agente que el backend acaba de declarar de otra forma, y una
 * insignia ausente se nota; una insignia equivocada, no.
 */
export function tipoConocido(tipo: string | undefined): TipoAgente | null {
  return tipo === "react" || tipo === "deep" || tipo === "grafo" ? tipo : null;
}

/** Fila de `GET /llm-assignments`: un agente, un subagente o una operacion. */
export interface Asignacion {
  target_type: TargetType;
  /** Para un subagente es `"<agente>/<subagente>"`. */
  target_id: string;
  /** Nombre a pintar. Para un subagente viene SIN el prefijo del padre. */
  label: string;
  /** Modelo que de VERDAD correria (lo dan los resolutores del runtime). */
  model_id: string;
  /** `asignado` = hay fila propia; `heredado` = la tiene el padre; `default`. */
  origen: OrigenAsignacion;
  /** `graph_id` del agente padre. Solo lo traen los subagentes. */
  parent?: string;
  /**
   * Caracteristicas del agente. **`null` en una operacion auxiliar**, que no es
   * un agente. Opcional en el tipo ademas de nullable: un panel abierto durante
   * un deploy puede recibir filas de un backend anterior que no la mandaba.
   */
  caracteristicas?: CaracteristicasAgente | null;
  updated_at: string | null;
  updated_by: string | null;
}

/**
 * Normaliza el `origen` que llega del backend.
 *
 * Un valor que este front no conozca se trata como `default` en vez de romper:
 * backend y front se despliegan juntos, pero un panel abierto durante un deploy
 * puede recibir un `origen` nuevo, y ahi lo unico inaceptable es una pantalla en
 * blanco. `default` es ademas el trato mas conservador: no afirma una asignacion
 * que no sabemos que exista, asi que no ofrece el boton de "volver al default"
 * sobre algo que quiza no se pueda quitar.
 */
export function origenConocido(origen: string): OrigenAsignacion {
  return origen === "asignado" || origen === "heredado" || origen === "default"
    ? origen
    : "default";
}

export function createModelosApi(fetchAuth: FetchAuth, apiUrl: string) {
  /**
   * Lee el `detail` del error de FastAPI y lo mete en el `ApiError`.
   *
   * No es cosmetico. El backend devuelve TRES 400 distintos a proposito (no
   * servible / deshabilitado / sin `tools`) y un 409 que dice si el modelo esta
   * asignado o fijado en una conversacion, porque **cada uno se arregla de una
   * forma**. Colapsarlos en "error al asignar" tiraria por tierra ese trabajo y
   * dejaria al admin sin saber que hacer.
   */
  async function fallo(res: Response, contexto: string): Promise<never> {
    let detalle: string | null = null;
    try {
      const cuerpo = await res.json();
      // FastAPI manda `detail` como texto en todos estos handlers; un 422 de
      // validacion lo manda como lista y ahi no hay mensaje util que enseñar.
      if (typeof cuerpo?.detail === "string") detalle = cuerpo.detail;
    } catch {
      /* cuerpo no-JSON (un 502 del proxy): nos quedamos con el status */
    }
    throw new ApiError(detalle || `${contexto}: ${res.status}`, res.status, detalle);
  }

  /**
   * Un `model_id` NO se URL-encodea: la ruta esta declarada `{model_id:path}`
   * justo porque los ids llevan barra (`openai/gpt-5.2`). Encodearlo convertiria
   * la barra en `%2F` y la ruta dejaria de casar para TODOS los modelos reales.
   */
  const ruta = (modelId: string) => `${apiUrl}/admin/llm-models/${modelId}`;

  /**
   * Busca en el catalogo de OpenRouter para dar de alta.
   *
   * Devuelve `{modelos, modoDirecto}` y no una lista pelada: sin
   * `OPENROUTER_API_KEY` el backend solo ofrece los `openai/*` —al resto no hay
   * a donde mandarlo—, asi que buscar "gemini" no devuelve nada mientras "gpt"
   * si. El flag deja al panel EXPLICARLO; sin el, una busqueda legitima que
   * no casa nada y una que el modo recorta se ven exactamente igual.
   *
   * Se tolera la forma vieja (un array) para que un front nuevo contra un
   * backend sin desplegar no se quede sin buscador: ahi `modoDirecto` es
   * `false`, que es lo que el panel asumia antes de existir el flag.
   */
  async function buscarCatalogo(q: string): Promise<ResultadoCatalogo> {
    const url = `${apiUrl}/admin/llm-models/catalog?q=${encodeURIComponent(q)}`;
    const res = await fetchAuth(url);
    if (!res.ok) return fallo(res, "Failed to search catalog");
    const cuerpo = await res.json();
    if (Array.isArray(cuerpo)) return { modelos: cuerpo, modoDirecto: false };
    return {
      modelos: cuerpo?.modelos ?? [],
      modoDirecto: cuerpo?.modo_directo === true,
    };
  }

  async function listarModelos(): Promise<ModeloCurado[]> {
    const res = await fetchAuth(`${apiUrl}/admin/llm-models`);
    if (!res.ok) return fallo(res, "Failed to list models");
    return res.json();
  }

  async function altaModelo(modelId: string, label?: string | null) {
    const res = await fetchAuth(`${apiUrl}/admin/llm-models`, {
      method: "POST",
      body: JSON.stringify({ model_id: modelId, label: label ?? null }),
    });
    if (!res.ok) return fallo(res, "Failed to create model");
    return res.json();
  }

  async function editarModelo(
    modelId: string,
    cambios: { label?: string | null; enabled?: boolean },
  ) {
    const res = await fetchAuth(ruta(modelId), {
      method: "PUT",
      body: JSON.stringify(cambios),
    });
    if (!res.ok) return fallo(res, "Failed to update model");
    return res.json();
  }

  async function borrarModelo(modelId: string) {
    const res = await fetchAuth(ruta(modelId), { method: "DELETE" });
    if (!res.ok) return fallo(res, "Failed to delete model");
    return res.json();
  }

  async function sincronizarModelos() {
    const res = await fetchAuth(`${apiUrl}/admin/llm-models/sync`, { method: "POST" });
    if (!res.ok) return fallo(res, "Failed to sync models");
    return res.json();
  }

  async function listarAsignaciones(): Promise<Asignacion[]> {
    const res = await fetchAuth(`${apiUrl}/admin/llm-assignments`);
    if (!res.ok) return fallo(res, "Failed to list assignments");
    return res.json();
  }

  /** `modelId: null` QUITA la asignacion y devuelve el target a su default. */
  async function asignarModelo(
    targetType: TargetType,
    targetId: string,
    modelId: string | null,
  ) {
    const res = await fetchAuth(
      // El `target_id` de un subagente lleva barra (`especificaciones/redactor`)
      // y la ruta esta declarada `{target_id:path}` justo por eso, igual que la
      // de `{model_id:path}`: encodearlo convertiria la barra en `%2F` y la ruta
      // dejaria de casar para TODOS los subagentes.
      `${apiUrl}/admin/llm-assignments/${targetType}/${targetId}`,
      { method: "PUT", body: JSON.stringify({ model_id: modelId }) },
    );
    if (!res.ok) return fallo(res, "Failed to assign model");
    return res.json();
  }

  return {
    buscarCatalogo,
    listarModelos,
    altaModelo,
    editarModelo,
    borrarModelo,
    sincronizarModelos,
    listarAsignaciones,
    asignarModelo,
  };
}

export type ModelosApi = ReturnType<typeof createModelosApi>;

// ---------------------------------------------------------------------------
// Derivadas puras (se prueban sin montar React, y las usa `SeccionModelos`)
// ---------------------------------------------------------------------------

/**
 * Umbral a partir del cual un snapshot de precios se considera VIEJO.
 *
 * **7 dias.** Justificacion: el backend sincroniza solo al arrancar y cada
 * hora, asi que en un servicio sano `synced_at` nunca pasa de una hora. Un
 * snapshot de mas de una semana significa que esa sincronizacion lleva dias sin
 * correr (o que el modelo se dio de alta y OpenRouter dejo de listarlo), y a
 * partir de ahi el precio que el panel muestra puede no ser el que se factura.
 * Se elige holgado a proposito: un umbral de horas convertiria cualquier
 * reinicio tardio o una caida corta de OpenRouter en un aviso que el admin
 * aprenderia a ignorar, y un aviso que se ignora no avisa de nada.
 */
export const DIAS_PRECIOS_VIEJOS = 7;

export type EstadoSync = "nunca" | "viejo" | "fresco";

/**
 * Estado del snapshot de un modelo.
 *
 * `nunca` NO es un caso de `viejo`, y la diferencia importa: un modelo sin
 * sincronizar jamas no tiene precios ni capacidades fiables de NINGUNA edad, y
 * lo que hay que hacer es sincronizar; uno viejo si los tuvo y lo que hay que
 * mirar es por que la sincronizacion dejo de correr. Tratar `null` como una
 * fecha muy antigua (`new Date(null)` es 1970, que es `viejo`) los confundiria
 * en el mismo aviso.
 */
export function estadoSincronizacion(syncedAt: string | null, ahora = Date.now()): EstadoSync {
  if (!syncedAt) return "nunca";
  const t = Date.parse(syncedAt);
  // Una fecha ilegible no se puede afirmar fresca: se trata como "nunca", que
  // es el aviso mas fuerte y el unico honesto cuando no sabemos la edad.
  if (Number.isNaN(t)) return "nunca";
  const dias = (ahora - t) / 86_400_000;
  return dias > DIAS_PRECIOS_VIEJOS ? "viejo" : "fresco";
}

/**
 * Modelos ofrecibles a un target, filtrados por CAPACIDAD.
 *
 * El backend rechaza con 400 un modelo sin `tools` para un **agente** (rompe un
 * ReAct), pero lo ACEPTA para una **operacion** auxiliar, que no usa tools. Asi
 * que el filtro tiene que distinguir los dos casos: con un filtro unico o bien
 * se ofrece algo que da 400, o bien se esconden modelos perfectamente validos
 * para las operaciones.
 *
 * Un **subagente** va con los agentes y no con las operaciones: los cinco que
 * hay usan tools, y el backend les aplica la misma guarda de `supports_tools`
 * (400 si el modelo no las declara).
 *
 * Los deshabilitados se caen para los tres: el PUT tambien los rechaza (400),
 * porque la resolucion filtra `enabled = TRUE` y la asignacion no tendria efecto.
 */
export function modelosParaTarget(
  modelos: ModeloCurado[],
  targetType: TargetType,
): ModeloCurado[] {
  return modelos.filter(
    (m) => m.enabled && (targetType === "operation" || m.supports_tools),
  );
}

/**
 * Texto de una `<option>` del desplegable de modelos: nombre + precio.
 *
 * Elegir modelo sin ver el precio es la decision CARA de esta pantalla —entre
 * el mas potente y el mas barato hay dos ordenes de magnitud— y el dato ya
 * viaja en `ModeloCurado`. Se compone aqui, en una funcion pura, porque lo
 * pintan las dos vistas (grafo y tabla) y dos plantillas distintas darian dos
 * formatos para el mismo dato.
 *
 * El precio va ENTRE PARENTESIS y detras del nombre, no delante: un `<select>`
 * nativo no se puede maquetar por dentro (ni columnas ni estilos en la opcion),
 * asi que lo unico que se puede controlar es el orden, y el nombre es por lo
 * que se busca. Se redondea a lo justo para leerlo de un vistazo, en vez de los
 * 4 decimales de la tabla del catalogo: ahi el numero es el dato, aqui es una
 * señal de magnitud, y `$0.0001 / $0.0004` alarga la opcion hasta desbordar el
 * ancho del desplegable sin decir mas que `$0.0001`.
 *
 * Si OpenRouter no declara precio NO se inventa un "gratis": se omite el
 * parentesis entero. Un `$0 / $0` se leeria como que ese modelo no cuesta nada.
 */
export function etiquetaModelo(m: ModeloCurado): string {
  const nombre = m.label || m.name || m.model_id;
  const entrada = m.pricing?.input;
  const salida = m.pricing?.output;
  if (entrada == null || salida == null) return nombre;
  return `${nombre} (${porMillon(entrada)} / ${porMillon(salida)})`;
}

/** Precio por millon, corto: sin ceros de relleno y sin notacion cientifica. */
function porMillon(valor: number): string {
  // `toFixed(4)` y no `toString()`: un precio como 5e-7 se serializaria en
  // notacion cientifica y `$5e-7` no se lee como un precio. Los ceros finales
  // se quitan despues para que 1.75 no salga como `$1.7500`.
  const texto = valor.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  return `$${texto}`;
}
